// ============================================================
// HOOKED — HeroAngler.js        LYRAEI & LAOSTRE, FISHING
//
// caP'n, 2026-09-25: "replace the character for Lyraei and Laostre".
//
// ⚑ THE LAW OF ONE BODY. These are TERROR'S bodies, the same ones
//   the Pier walks: lyraeiModel3D.jsx · laostreModel3D.jsx and
//   sculptGate.jsx are BYTE-IDENTICAL copies (tools/syncPier.mjs
//   copies them and md5-checks them). NEVER edit those three here —
//   re-run the sync when Terror moves on. Their glbs are the
//   gamemaster masters.
// ⚑ THEY OWN THE BODY, WE OWN THE POSE. This file only chooses the
//   body, sizes it exactly as the Pier does, and hands it the same
//   `ctl` = { facing, speed } the Pier hands it.
// ⚑ THE FAIL-SAFE LAW. While a glb loads, if it fails, or when
//   <FrameWatch/> sees the lake can't keep up, Hooked's own painted
//   angler (Angler3D) stands in. The lake never shows nobody.
//
// ⚠ The Outfitter's clothes do not show on these bodies yet — the
//   gear still WORKS (tackle is functional, never cosmetic). caP'n:
//   "we can do that later, I'll get back to you on that".
// ============================================================

import { useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { LyraeiSculpted } from "./lyraeiModel3D";
import { LaostreSculpted } from "./laostreModel3D";
import { failSafe } from "./sculptGate";
import { Angler3D } from "./Angler3D";

// The Pier's own heights (pierFolk3D): a person is 1.5 there, Terror's
// proportions kept exactly — Lyraei 1.3, Laostre 1.6, × 1.5/1.3.
export const PIER_PERSON = 1.5 / 1.3;
export const LYRAEI_H = 1.3 * PIER_PERSON;
export const LAOSTRE_H = 1.6 * PIER_PERSON;

// the stand-in: Hooked's painted angler, parked at the body's own origin
const ZERO = { current: new THREE.Vector3(0, 0, 0) };
function PaintedAngler({ ctl }) {
  return <Angler3D posRef={ZERO} yawRef={ctl.yawRef} speedRef={ctl.speedRef} mode={ctl.mode || "walk"} outfit={ctl.outfit || {}} />;
}
const LyraeiHere = failSafe(LyraeiSculpted, PaintedAngler, LYRAEI_H);
const LaostreHere = failSafe(LaostreSculpted, PaintedAngler, LAOSTRE_H);

// ⭐ The player. `posRef` places it; in the boat `seatRef` does instead (PierSkiff
// writes its seat there every frame), so ONE body stays mounted for the whole
// session — boarding never re-creates it (QA 2026-09-25: every board/dock used to
// build a fresh skinned clone and ~20 materials that were never freed).
// `yawRef` is Hooked's atan2(dx, dz) facing, the same convention the Pier uses.
export function HeroAngler({ gender = "f", posRef, yawRef, speedRef, seatRef, afloat = false, mode = "walk", outfit }) {
  const g = useRef();
  const stillSpeed = useMemo(() => ({ current: 0 }), []);
  const ctl = useMemo(() => ({ facing: { x: 0, y: 1 }, speed: 0 }), []);
  const shadowed = useRef(0);
  ctl.yawRef = yawRef; ctl.speedRef = speedRef || stillSpeed; ctl.mode = mode; ctl.outfit = outfit;
  useFrame(() => {
    const o = g.current; if (!o) return;
    const seat = seatRef && seatRef.current;
    let y;
    if (afloat && seat && seat.on) { o.position.set(seat.x, seat.y, seat.z); y = seat.yaw; ctl.speed = 0; }
    else {
      if (posRef && posRef.current) o.position.copy(posRef.current);
      y = yawRef.current || 0;
      ctl.speed = speedRef && speedRef.current > 0.05 ? 1 : 0;
    }
    ctl.facing.x = Math.sin(y); ctl.facing.y = Math.cos(y);
    // the sculpted bodies arrive async and don't cast shadows themselves (their
    // files are byte-identical copies we never edit): turn it on from out here
    if (shadowed.current < 240 && (++shadowed.current % 30 === 0)) {
      o.traverse((m) => { if (m.isMesh && !/_ink$/.test(m.name)) m.castShadow = true; });
    }
  });
  return (
    <group ref={g}>
      {gender === "m" ? <LaostreHere ctl={ctl} /> : <LyraeiHere ctl={ctl} />}
      {/* ⚑ PRIME §1.18: the lantern's light lives OUTSIDE the body, so it survives
          real art. The painted angler carried it; the sculpted ones don't — so it
          walks here, at the hip, where his lantern hung (Angler3D's own numbers). */}
      <pointLight position={[0.34, 0.95, 0.12]} intensity={5.6} distance={11.5} decay={1.35} color="#ffc978" castShadow={false} />
      {/* CONTACT SHADOW — the weight on the boards (Angler3D's own blob) */}
      {!afloat && (
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.012, 0]} raycast={() => null}>
          <circleGeometry args={[0.4, 20]} />
          <meshBasicMaterial color="#000000" transparent opacity={0.42} depthWrite={false} />
        </mesh>
      )}
    </group>
  );
}

// the body choice lives in its own key: the angler's save is untouched
const BODY_KEY = "hooked_body_v1";
export function loadBody() {
  try { return localStorage.getItem(BODY_KEY) === "m" ? "m" : "f"; } catch (e) { return "f"; }
}
export function saveBody(g) {
  try { localStorage.setItem(BODY_KEY, g); } catch (e) { /* private mode: it just won't remember */ }
}
